/* eslint-disable @typescript-eslint/no-use-before-define */
import {
    Action,
    ActionReducerMap,
    createFeatureSelector,
    createSelector,
    StoreModule,
} from '@ngrx/store';
import { RideGroup } from 'src/app/models/ride-group';
import { RideGroupRiderStatus } from 'src/app/models/ride-group-rider-status';
import {
    AddOfflineRideGroup,
    AddRideGroup,
    OfflineRideGroupActions,
    RemoveOfflineRideGroup,
    RemoveRideGroup,
    RideGroupActions,
    RideGroupRiderActions,
    SetActiveRideGroup,
    SetActiveRideGroupLeader,
    SetActiveRideGroupRider,
    SetOfflineRideGroups,
    SetRideGroupRiders,
    SetRideGroups,
} from './ride-group.actions';

export type RideGroupFollowingState = 'None' | 'RideGroup' | 'Leader';
export interface RideGroupState {
    activeRideGroup?: RideGroup;
    activeRideGroupLeader?: RideGroupRiderStatus;
    activeRideGroupRider?: RideGroupRiderStatus;
    followingState: RideGroupFollowingState;
    offlineRideGroups: RideGroup[];
    rideGroups: RideGroup[];
    rideGroupRiders: RideGroupRiderStatus[];
}

export const reducerMap: ActionReducerMap<RideGroupState> = {
    activeRideGroup: activeRideGroupReducer,
    activeRideGroupLeader: activeRideGroupLeaderReducer,
    activeRideGroupRider: activeRideGroupRiderReducer,
    followingState: followingStateReducer,
    offlineRideGroups: offlineRideGroupsReducer,
    rideGroupRiders: rideGroupRidersReducer,
    rideGroups: rideGroupsReducer,
};

export const selectRideGroupState =
    createFeatureSelector<RideGroupState>('rideGroups');
export const selectActiveRideGroup = createSelector(
    selectRideGroupState,
    (state) => state.activeRideGroup
);
export const selectActiveRideGroupLeader = createSelector(
    selectRideGroupState,
    (state) => state.activeRideGroupLeader
);
export const selectActiveRideGroupRider = createSelector(
    selectRideGroupState,
    (state) => state.activeRideGroupRider
);
export const selectFollowingState = createSelector(
    selectRideGroupState,
    (state) => state.followingState
);
export const selectOfflineRideGroups = createSelector(
    selectRideGroupState,
    (state) => state.offlineRideGroups
);
export const selectRideGroups = createSelector(
    selectRideGroupState,
    (state) => state.rideGroups
);
export const selectRideGroupRiders = createSelector(
    selectRideGroupState,
    (state) => state.rideGroupRiders
);

export const rideGroupStoreModule = StoreModule.forFeature(
    'rideGroups',
    reducerMap
);

export function activeRideGroupReducer(
    state: RideGroup,
    action: Action
): RideGroup | undefined {
    switch (action.type) {
        case RideGroupActions.setActiveRideGroup:
            return (action as SetActiveRideGroup).rideGroup?.clone();
        default:
            return state;
    }
}

export function activeRideGroupLeaderReducer(
    state: RideGroupRiderStatus,
    action: Action
): RideGroupRiderStatus | undefined {
    switch (action.type) {
        case RideGroupActions.setActiveRideGroupLeader: {
            const newLeader = (action as SetActiveRideGroupLeader).leader;
            return newLeader && newLeader.clone();
        }
        default:
            return state;
    }
}

export function activeRideGroupRiderReducer(
    state: RideGroupRiderStatus,
    action: Action
): RideGroupRiderStatus | undefined {
    switch (action.type) {
        case RideGroupActions.setActiveRideGroupRider: {
            const newRider = (action as SetActiveRideGroupRider).rider;
            return newRider && newRider.clone();
        }
        case RideGroupRiderActions.setRideGroupRiders: {
            if (!state) {
                return state;
            }

            const riders = (action as SetRideGroupRiders).riders;
            const updatedRider = (riders || []).find(
                (rider) => rider.id === state.id
            );

            if (!updatedRider) {
                return undefined;
            }

            const clonedRider = state.clone();
            clonedRider.time = updatedRider.time;
            const newPoints =
                updatedRider.leaderLine ||
                updatedRider.geoCoordinateAsLeaderLine;
            clonedRider.appendToLeaderLine(newPoints);

            return clonedRider;
        }
        default:
            return state;
    }
}

export function followingStateReducer(
    state: RideGroupFollowingState,
    action: Action
): RideGroupFollowingState {
    switch (action.type) {
        case RideGroupActions.startFollowingRideGroup:
            return 'RideGroup';
        case RideGroupActions.startFollowingLeader:
            return 'Leader';
        case RideGroupActions.stopFollowingRideGroup:
            return state === 'RideGroup' ? 'None' : state;
        case RideGroupActions.stopFollowingLeader:
            return state === 'Leader' ? 'None' : state;
        case RideGroupActions.stopFollowing:
            return 'None';
        default:
            return state;
    }
}

export function offlineRideGroupsReducer(
    state: RideGroup[],
    action: Action
): RideGroup[] {
    let newRideGroup: RideGroup;

    switch (action.type) {
        case OfflineRideGroupActions.clearAllRideGroups:
            return [];

        case OfflineRideGroupActions.addRideGroup: {
            newRideGroup = (action as AddOfflineRideGroup).rideGroup;
            const otherRideGroups = state.filter(
                (rideGroup) => rideGroup.groupId !== newRideGroup.groupId
            );
            return [...otherRideGroups, newRideGroup];
        }
        case OfflineRideGroupActions.removeRideGroup: {
            newRideGroup = (action as RemoveOfflineRideGroup).rideGroup;
            const filteredState = state.filter(
                (i) => i.groupId !== newRideGroup.groupId
            );
            return filteredState;
        }

        case OfflineRideGroupActions.setRideGroups: {
            const groups = (action as SetOfflineRideGroups).rideGroups;
            return [...groups];
        }
        default:
            return state;
    }
}

export function rideGroupsReducer(
    state: RideGroup[],
    action: Action
): RideGroup[] {
    let newRideGroup: RideGroup;

    switch (action.type) {
        case RideGroupActions.clearAllRideGroups:
            return [];

        case RideGroupActions.addRideGroup: {
            newRideGroup = (action as AddRideGroup).rideGroup;
            const otherRideGroups = state.filter(
                (rideGroup) => rideGroup.groupId !== newRideGroup.groupId
            );
            return [...otherRideGroups, newRideGroup];
        }
        case RideGroupActions.removeRideGroup: {
            newRideGroup = (action as RemoveRideGroup).rideGroup;
            const filteredState = state.filter(
                (i) => i.groupId !== newRideGroup.groupId
            );
            return filteredState;
        }

        case RideGroupActions.setRideGroups: {
            const groups = (action as SetRideGroups).rideGroups;
            return [...groups];
        }
        default:
            return state;
    }
}

export function rideGroupRidersReducer(
    state: RideGroupRiderStatus[],
    action: Action
): RideGroupRiderStatus[] {
    switch (action.type) {
        case RideGroupRiderActions.setRideGroupRiders:
            return [...((action as SetRideGroupRiders).riders || [])];
        default:
            return state;
    }
}
